import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoWordmark } from "@/components/Logo";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin/insights");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#e6e1d5] rounded-xl px-4 py-3.5 text-sm text-[#1b2a4e] placeholder:text-[#8a93a8] focus:outline-none focus:ring-2 focus:ring-[#a8824f] focus:border-transparent transition-all";

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            <LogoWordmark dark />
          </div>
          <p className="font-mono-alt text-[10px] uppercase tracking-[0.35em] text-[#a8824f]">
            Insights Studio
          </p>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="bg-white border border-[#e6e1d5] rounded-3xl p-8 sm:p-10 shadow-[0_24px_70px_rgba(27,42,78,0.1)]"
        >
          <h1 className="font-display text-2xl text-[#1b2a4e] mb-6">Sign in to publish</h1>
          {error && (
            <p data-testid="admin-login-error" className="mb-5 text-sm text-[#b0483c] bg-[#b0483c]/10 border border-[#b0483c]/20 rounded-xl px-4 py-3">
              {error}
            </p>
          )}
          <label htmlFor="admin-email-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Email</label>
          <input
            id="admin-email-input"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@complywise.co.uk"
            className={`${inputCls} mb-5`}
          />
          <label htmlFor="admin-password-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Password</label>
          <input
            id="admin-password-input"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your admin password"
            className={`${inputCls} mb-7`}
          />
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2.5 bg-[#1b2a4e] hover:bg-[#a8824f] disabled:opacity-50 text-white text-sm font-semibold px-8 py-4 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
          >
            {busy ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />}
            {busy ? "Signing in..." : "Sign In"}
          </button>
          <p className="mt-6 text-center">
            <a data-testid="admin-login-back-link" href="/" className="text-xs text-[#8a93a8] hover:text-[#a8824f] transition-colors">
              ← Back to website
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
